import { isP2PModuleEnabled } from './entitlements';
import type { P2PActivationInput, P2PActivationResult } from './types';

const P2P_REQUEST_PATTERN = /\b(p2p|peer[\s-]*to[\s-]*peer)\b/i;
const P2P_STATUS_PATTERN = /\b(requested|open|opened|scheduled|failed|timed[\s-]*out|completed|available)\b/i;

function hasSubmittedRequest(input: P2PActivationInput): boolean {
  return Boolean(input.requestSubmittedAt);
}

function hasPayerResponse(input: P2PActivationInput): boolean {
  return Boolean(input.payerResponseDate || input.payerResponseStatus || input.denialText);
}

function isDenied(input: P2PActivationInput): boolean {
  const statusValues = [
    input.authorizationStatus,
    input.payerNormalizedStatus,
    input.payerResponseStatus,
  ].map(value => (value || '').toLowerCase());

  return statusValues.some(value => value.includes('denied') || value === 'denial');
}

function hasExplicitP2PRequest(input: P2PActivationInput): boolean {
  const status = input.p2pStatus || '';
  const denialText = input.denialText || '';

  return (
    (P2P_REQUEST_PATTERN.test(status) && P2P_STATUS_PATTERN.test(status)) ||
    P2P_REQUEST_PATTERN.test(denialText)
  );
}

export function evaluateP2PActivationGate(input: P2PActivationInput): P2PActivationResult {
  if (!isP2PModuleEnabled(input.entitlement)) {
    return { status: 'inactive', reason: 'module_disabled' };
  }

  if (input.workflowPhase === 'pre_auth_generation' || input.workflowPhase === 'post_op_generation') {
    return { status: 'inactive', reason: 'document_generation' };
  }

  if (!hasSubmittedRequest(input)) {
    return { status: 'inactive', reason: 'request_not_submitted' };
  }

  if (!hasPayerResponse(input)) {
    return { status: 'inactive', reason: 'payer_response_missing' };
  }

  if (!isDenied(input)) {
    return { status: 'inactive', reason: 'payer_not_denied' };
  }

  if (!hasExplicitP2PRequest(input)) {
    return { status: 'inactive', reason: 'p2p_not_requested' };
  }

  return {
    status: 'ready',
    trigger: 'denial_requested_p2p',
    requestKind: input.requestKind,
  };
}
